/**
 * Color contrast utilities for ensuring text readability
 */

/**
 * Parse a hex color to RGB values
 */
export const hexToRgb = (hex) => {
  if (!hex) return null;
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return null;

  return {
    r: parseInt(cleanHex.substr(0, 2), 16),
    g: parseInt(cleanHex.substr(2, 2), 16),
    b: parseInt(cleanHex.substr(4, 2), 16)
  };
};

/**
 * Calculate relative luminance of a color (WCAG formula)
 */
export const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Check if a color is light (brightness > 155)
 */
export const isLightColor = (color) => {
  const rgb = hexToRgb(color);
  if (!rgb) return false;
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 155;
};

/**
 * Calculate contrast ratio between two colors (WCAG formula)
 * Returns a value between 1 and 21
 * WCAG AA requires 4.5:1 for normal text, 3:1 for large text
 */
export const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Get a readable text color for the given accent and background
 * If accent has poor contrast, returns a darker alternative
 * @param {string} accentColor - The desired accent color
 * @param {string} backgroundColor - The background color (defaults to white)
 * @param {number} minContrast - Minimum contrast ratio (default 4.5 for WCAG AA)
 * @returns {string} - A readable color (accent if good contrast, or dark gray fallback)
 */
export const getReadableAccentColor = (accentColor, backgroundColor = '#ffffff', minContrast = 4.5) => {
  const contrast = getContrastRatio(accentColor, backgroundColor);

  if (contrast >= minContrast) {
    return accentColor;
  }

  // Accent has poor contrast - darken it or use fallback
  // Try to darken the accent color
  const rgb = hexToRgb(accentColor);
  if (rgb) {
    // Darken the color by reducing RGB values by 40%
    const darkerR = Math.floor(rgb.r * 0.6);
    const darkerG = Math.floor(rgb.g * 0.6);
    const darkerB = Math.floor(rgb.b * 0.6);
    const darkerHex = `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;

    // Check if darkened version has good contrast
    if (getContrastRatio(darkerHex, backgroundColor) >= minContrast) {
      return darkerHex;
    }
  }

  // Fallback to a neutral dark color
  return isLightColor(backgroundColor) ? '#374151' : '#e5e7eb';
};

export default {
  hexToRgb,
  getLuminance,
  isLightColor,
  getContrastRatio,
  getReadableAccentColor
};
